import React from 'react';
import { isSameHour, startOfHour } from 'date-fns';
import { useStatistics } from '../queries';
import { AreaChart } from '@tremor/react';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import { useStatisticsContext } from '../Dashboard';
import { formatTimestamp, formatValue } from '../dates';
import ChartMeta from './ChartMeta';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import Spinner from '@dashboard/components/Spinner';

export default function ActiveSubscribers() {
  const { unit, range } = useStatisticsContext();
  const { data, isLoading } = useStatistics('activeSubscribers');

  const activeSubscribersData = data?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    'Active subscribers': formatValue(timestamp, value),
  }));

  const maxValue = data && Math.max(...data?.map(({ value }) => value), 0);
  const minValue = data && Math.min(...data?.map(({ value }) => value), 0);

  let currentActiveSubscribers = data?.[data.length - 1]?.value;

  if (unit === 'hour') {
    const currentHour = startOfHour(new Date());
    const currentHourData = data?.find(({ timestamp }) =>
      isSameHour(new Date(timestamp), currentHour),
    );

    if (currentHourData) {
      currentActiveSubscribers = currentHourData?.value;
    }
  }

  return (
    <Card variant="none">
      <CardHeader variant="card">
        <CardHeaderTitle variant="card">
          <div>
            <div className="text-base font-normal">
              Active subscribers{' '}
              <InfoTooltip>
                This is the total count of your active and overdue subscribers. It does not include
                trials. A subscriber with multiple subscriptions is counted as one subscriber.
              </InfoTooltip>
            </div>

            <div className="text-xl mt-1.5 font-normal">{currentActiveSubscribers}</div>
          </div>
        </CardHeaderTitle>
      </CardHeader>

      <CardContent variant="root">
        {isLoading ? (
          <div className="flex items-center justify-center h-44">
            <Spinner />
          </div>
        ) : (
          <div className="relative pl-8">
            {activeSubscribersData && <ChartMeta maxValue={maxValue} minValue={minValue} />}

            <AreaChart
              className="mt-4 h-44"
              data={activeSubscribersData}
              categories={['Active subscribers']}
              index="timestamp"
              colors={['amber']}
              showAnimation
              allowDecimals={false}
              showLegend={false}
              showGridLines={false}
              showYAxis={false}
              showXAxis
              startEndOnly
              showGradient
              padding={{ left: 16, right: 16 }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
